import { z } from 'zod';
import { csv } from './query.js';
// The lookup — ONE bounded read per big entity (ADR-078 §6): the 20 best rows by
// the typed search (ADR-072) and `has_more` past them, or the rows of given ids
// (a value that arrived by id — a link, a saved view, a form opened with a
// value). The surface NAMES its rule and the server applies it; a rule says what
// each status does (picked · greyed · not shown). This file holds the shape the
// three entities share; each entity's rules and row live beside its own wire
// (sites/lookup.ts · assets/lookup.ts · users/lookup.ts).
/** The answer's cap — and the cap on the ids one request may name: ONE number,
 *  so no lookup answer ever holds more than 20 rows (the 5 KB rule). */
export const LOOKUP_CAP = 20;
/** The typed text — the same cap as the lists' `q`. */
export const LookupTextSchema = z.string().trim().max(64);
/** The ids a request names — at most LOOKUP_CAP; a caller with more asks in batches. */
export const LookupIdsSchema = csv(z.string().min(1).max(16)).pipe(z.array(z.string()).max(LOOKUP_CAP, `at most ${LOOKUP_CAP} ids a request`).optional());
/** `q` and `ids` never travel together: a request either searches or names. */
export function refineLookupQuery(v, ctx) {
    if (v.q && v.ids?.length)
        ctx.addIssue({ code: 'custom', message: '`q` and `ids` never travel together', path: ['ids'] });
}
/** A suggestion GROUP holds at most this many rows before anything is typed
 *  (ADR-080 §6): the server orders the groups first and caps each at rest; a
 *  group's other rows take their place in the A–Z; once something is typed a
 *  group's matches all come first. */
export const LOOKUP_GROUP_CAP = 5;
/** What every lookup row carries beside its face: whether the rule lets it be
 *  picked (false = GREYED — the thing is paused, its status says why) and its
 *  suggestion GROUP — the kind's own reason the row leads the start list
 *  (sites `followed` · a filter's people `me` · the devices `fronts`), null
 *  for an ordinary row; the server orders the groups
 *  first, at most LOOKUP_GROUP_CAP rows each at rest, and a row is never in
 *  two groups. Nothing says HOW a row matched (the field-exposure rule). */
export const LookupRowBaseSchema = z.object({
    id: z.string().max(16),
    pickable: z.boolean(),
    group: z.string().min(1).max(32).nullable(),
});
/** The answer: the rows (≤ LOOKUP_CAP) and whether the cap cut it. */
export function lookupResponseSchema(row) {
    return z.object({ items: z.array(row).max(LOOKUP_CAP), has_more: z.boolean() }).strict();
}
